/**
 * ContactForm
 *
 * Collects contact info with inline email validation and theme-aware styles.
 * Reports validity via onRepsLoaded.
 */
import React, { useEffect, useState } from 'react';
import { Label } from '../../../components/ui/inputs/label';
import { Input } from '../../../components/ui/inputs/input';

export function ContactForm({ 
  formData, 
  handleInputChange, 
  theme, 
  themes,
  customTheme,
  getTextStyle,
  getSubtextStyle,
  onRepsLoaded = () => {}
}) {
  const [emailError, setEmailError] = useState('');

  // Add debugging to see what theme is being passed
  useEffect(() => {
    console.log('ContactForm theme debug:', { theme, themes: Object.keys(themes || {}) });
  }, [theme, themes]);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email input changes with validation
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    handleInputChange(e);
    
    // Only validate if there's a value (don't show error when field is empty)
    if (value.trim()) {
      if (!validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  };

  // Form validation for contact information
  const isFormValid = Boolean(
    formData.firstName?.trim() && 
    formData.lastName?.trim() && 
    formData.email?.trim() && 
    validateEmail(formData.email)
  );

  // Update parent component about form validity
  useEffect(() => {
    if (typeof onRepsLoaded === 'function') {
      onRepsLoaded(isFormValid);
    }
  }, [formData.firstName, formData.lastName, formData.email, isFormValid, onRepsLoaded]);

  return (
    <div>
      <h2 className={`text-2xl font-bold ${
        theme 
          ? theme === 'custom' 
              ? 'text-white' 
              : themes[theme]?.text || 'text-gray-900'
          : 'text-gray-900'
      }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
        Contact Information
      </h2>
      <p className={`mt-2 text-sm ${
        theme 
          ? theme === 'custom' 
              ? 'text-white/80' 
              : themes[theme]?.subtext || 'text-gray-600'
          : 'text-gray-600'
      }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
        Please provide your contact details.
      </p>
      <div className="mt-8 space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <Label 
              htmlFor="firstName" 
              className={
                theme 
                  ? theme === 'custom' 
                      ? 'text-white' 
                      : themes[theme]?.text || 'text-gray-900'
                  : 'text-gray-900'
              }
              style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}
            >
              First name
            </Label>
            <Input
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className={`mt-2 ${
                theme 
                  ? theme === 'custom' 
                    ? '!border-white/20 !bg-white/20 !text-white placeholder:text-white/50 focus:ring-white/20' 
                    : `!border-white/20 !bg-white/20 !text-white placeholder:text-white/50 focus:ring-white/20`
                  : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-500'
              }`}
              style={theme === 'custom' && getTextStyle ? getTextStyle() : theme ? { 
                color: 'white !important',
                backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                borderColor: 'rgba(255, 255, 255, 0.2) !important'
              } : {}}
            />
          </div>
          <div>
            <Label 
              htmlFor="lastName" 
              className={
                theme 
                  ? theme === 'custom' 
                      ? 'text-white' 
                      : themes[theme]?.text || 'text-gray-900'
                  : 'text-gray-900'
              }
              style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}
            >
              Last name
            </Label>
            <Input
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className={`mt-2 ${
                theme 
                  ? theme === 'custom' 
                    ? '!border-white/20 !bg-white/20 !text-white placeholder:text-white/50 focus:ring-white/20' 
                    : `!border-white/20 !bg-white/20 !text-white placeholder:text-white/50 focus:ring-white/20`
                  : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-500'
              }`}
              style={theme === 'custom' && getTextStyle ? getTextStyle() : theme ? { 
                color: 'white !important',
                backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                borderColor: 'rgba(255, 255, 255, 0.2) !important'
              } : {}}
            />
          </div>
        </div>
        <div>
          <Label 
            htmlFor="email" 
            className={
              theme 
                ? theme === 'custom' 
                    ? 'text-white' 
                    : themes[theme]?.text || 'text-gray-900'
                : 'text-gray-900'
            }
            style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}
          >
            Email address
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleEmailChange}
            required
            className={`mt-2 ${
              theme 
                ? theme === 'custom' 
                  ? '!border-white/20 !bg-white/20 !text-white placeholder:text-white/50 focus:ring-white/20' 
                  : `!border-white/20 !bg-white/20 !text-white placeholder:text-white/50 focus:ring-white/20`
                : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-500'
            }`}
            style={theme === 'custom' && getTextStyle ? getTextStyle() : theme ? { 
              color: 'white !important',
              backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
              borderColor: 'rgba(255, 255, 255, 0.2) !important'
            } : {}}
          />
          {emailError && (
            <p className={`mt-1 text-xs ${
              theme
                ? theme === 'custom' 
                  ? 'text-red-300' 
                  : 'text-red-300'
                : 'text-red-500'
            }`}>
              {emailError}
            </p>
          )}
        </div>
        
        {/* Contact information disclaimer */}
        <div className={`text-xs ${
          theme 
            ? theme === 'custom'
              ? 'text-white/80' 
              : themes[theme]?.subtext || 'text-white/80'
            : 'text-gray-500'
        } opacity-80 p-3 rounded-lg ${
          theme 
            ? 'border border-white/10 bg-white/5' 
            : 'border border-gray-200 bg-gray-50'
        }`}
        style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
          We use your name to give credit for your video response. Your information helps us authenticate responses and will be displayed alongside your video submission.
        </div>
      </div>
    </div>
  );
}